<template>
  <div class="appointment-stats">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">Appointments booked via Teamio</h2>
      <p class="page-subtitle">
        Online &amp; AI bookings recorded for this office. This is a booking log — it
        does not track whether the appointment was later completed, cancelled, or a no-show.
      </p>
    </div>

    <!-- Stat cards — ONLY metrics the booking log supports (no fake outcomes) -->
    <div class="hero-stats-bar">
      <div class="hero-stat-card">
        <div class="stat-icon appointments-icon">
          <i class="fa fa-calendar"></i>
        </div>
        <div class="stat-content">
          <!-- Loading / not-available / error states replace the number entirely;
               a real number (0 included) only renders when access is 'ok'. -->
          <div v-if="isLoading" class="stat-state stat-state-loading">
            <div class="spinner spinner-sm"></div>
          </div>
          <div v-else-if="accessState === 'not-available'" class="stat-state stat-state-muted">Not available</div>
          <div v-else-if="accessState === 'error'" class="stat-state stat-state-error">Couldn't load</div>
          <template v-else>
            <div class="stat-value">{{ fmt(stats.range) }}</div>
            <div class="stat-label">Booked via Teamio · {{ rangeSubline }}</div>
          </template>
        </div>
      </div>

      <div class="hero-stat-card">
        <div class="stat-icon today-icon">
          <i class="fa fa-calendar-check-o"></i>
        </div>
        <div class="stat-content">
          <div v-if="isLoading" class="stat-state stat-state-loading">
            <div class="spinner spinner-sm"></div>
          </div>
          <div v-else-if="accessState === 'not-available'" class="stat-state stat-state-muted">Not available</div>
          <div v-else-if="accessState === 'error'" class="stat-state stat-state-error">Couldn't load</div>
          <template v-else>
            <div class="stat-value">{{ fmt(stats.today) }}</div>
            <div class="stat-label">Booked today</div>
          </template>
        </div>
      </div>
    </div>

    <!-- Booking Trend (real) — last 30 site-local days from `trend` -->
    <div class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-bar-chart panel-icon"></i>
          Bookings — last 30 days
        </h3>
      </div>
      <div class="panel-body chart-container">
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <span>Loading trend...</span>
        </div>
        <div v-else-if="accessState === 'not-available'" class="empty-state">
          <div class="empty-icon"><i class="fa fa-lock"></i></div>
          <div class="empty-title">Not available</div>
          <div class="empty-text">You don't have access to this site's booking stats.</div>
        </div>
        <div v-else-if="accessState === 'error'" class="empty-state">
          <div class="empty-icon"><i class="fa fa-exclamation-triangle"></i></div>
          <div class="empty-title">Couldn't load</div>
          <div class="empty-text">Something went wrong loading the booking trend. Please try again.</div>
        </div>
        <div v-else-if="trendEmpty" class="empty-state">
          <div class="empty-icon"><i class="fa fa-bar-chart"></i></div>
          <div class="empty-title">No bookings in the last 30 days</div>
          <div class="empty-text">New online and AI bookings will appear here.</div>
        </div>
        <!-- Inline CSS bar chart — same value/max*100% height approach as
             Huddle ProductionTrends; no chart library, fully data-bound. The bars
             themselves are untouched; we only THIN the x-axis tick labels (show every
             Nth + always the most-recent day) so 30 labels don't crowd into mush —
             especially on a phone. An empty slot keeps the bar's column width so the
             bars stay evenly spaced even where the label is hidden. -->
        <div v-else class="trend-chart" role="img" aria-label="Bookings per day for the last 30 days">
          <div
            v-for="(bar, i) in trendBars"
            :key="i"
            class="trend-bar-col"
            :title="`${bar.dateLabel}: ${bar.count} ${bar.count === 1 ? 'booking' : 'bookings'}`"
          >
            <div class="trend-bar-track">
              <div
                class="trend-bar-fill"
                :class="{ 'is-zero': bar.count === 0 }"
                :style="{ height: bar.heightPct + '%' }"
              ></div>
            </div>
            <div class="trend-bar-label">{{ showTrendLabel(i) ? bar.label : '' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Bookings Table (real) -->
    <div class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-list panel-icon"></i>
          Recent bookings
        </h3>
      </div>
      <div class="panel-body table-panel">
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <span>Loading bookings...</span>
        </div>
        <div v-else-if="accessState === 'not-available'" class="empty-state">
          <div class="empty-icon"><i class="fa fa-lock"></i></div>
          <div class="empty-title">Not available</div>
          <div class="empty-text">You don't have access to this site's bookings.</div>
        </div>
        <div v-else-if="accessState === 'error'" class="empty-state">
          <div class="empty-icon"><i class="fa fa-exclamation-triangle"></i></div>
          <div class="empty-title">Couldn't load</div>
          <div class="empty-text">Something went wrong loading recent bookings. Please try again.</div>
        </div>
        <div v-else-if="recentEmpty" class="empty-state">
          <div class="empty-icon"><i class="fa fa-calendar-o"></i></div>
          <div class="empty-title">No bookings yet</div>
          <div class="empty-text">Bookings will appear here once patients schedule online or via the AI assistant.</div>
        </div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Type</th>
              <th>Provider</th>
              <th>Date</th>
              <th>Time</th>
              <th>Patient type</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(booking, i) in recentRows" :key="i">
              <td class="patient-cell">{{ booking.name }}</td>
              <td>{{ booking.type }}</td>
              <td>{{ booking.provider }}</td>
              <td>{{ booking.date }}</td>
              <td>{{ booking.time }}</td>
              <td>{{ booking.patientType }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import mixin from '../../../mixin.js';
import {
  formatStatNumber,
  buildTrendBars,
  trendIsEmpty,
  formatRecentBooking,
  recentIsEmpty,
  trendLabelStride,
  shouldShowTrendLabel,
  rangeLabel,
} from '@/utils/dashboardStats';

export default {
  name: 'AppointmentStats',
  mixins: [mixin],
  data() {
    return {
      // trend/recent are NOT stored in the shared botAdmin tile state (the tile only
      // needs total/today), so the page holds them locally from the action's return.
      trend: [],
      recent: [],
      // Presentational only — drives how many x-axis labels we show so 30 labels
      // don't crowd on a narrow screen. Updated on resize; never touches the data.
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  computed: {
    // total/today + loading/access for the appointments tile come from the shared
    // botAdmin store (committed by fetchAppointmentsStats), so a 403/error here is
    // surfaced honestly and is consistent with the Dashboard's appointment tile.
    ...mapGetters('botAdmin', {
      dashboardStats: 'dashboardStats',
      dashboardLoading: 'dashboardLoading',
      dashboardAccess: 'dashboardAccess',
      dashboardRange: 'dashboardRange',
    }),
    stats() {
      return this.dashboardStats.appointments; // { total, today, range }
    },
    // Human label for the selected range — labels the headline range card so it's
    // clear which window the count covers (e.g. "All time" / "Last 7 days").
    rangeSubline() {
      return rangeLabel(this.dashboardRange);
    },
    isLoading() {
      return this.dashboardLoading.appointments;
    },
    accessState() {
      return this.dashboardAccess.appointments; // 'ok' | 'not-available' | 'error'
    },
    trendBars() {
      return buildTrendBars(this.trend);
    },
    trendEmpty() {
      return trendIsEmpty(this.trend);
    },
    // How aggressively to thin the x-axis labels: fewer labels on narrow screens so
    // the 'M/D' ticks never overlap. Purely presentational — does not affect bars.
    trendLabelTarget() {
      if (this.viewportWidth < 480) return 5;
      if (this.viewportWidth < 768) return 7;
      return 10;
    },
    trendStride() {
      return trendLabelStride(this.trendBars.length, this.trendLabelTarget);
    },
    recentRows() {
      return (this.recent || []).map(formatRecentBooking);
    },
    recentEmpty() {
      return recentIsEmpty(this.recent);
    },
  },
  watch: {
    // The siteID may hydrate after mount (customer store loads async); re-fetch when
    // it first becomes available so the page isn't stuck on a stale empty state.
    siteID(newId, oldId) {
      if (newId && newId !== oldId) {
        this.fetchStats();
      }
    },
    // Re-fetch when the shared date-range changes (the picker in AdminLayout commits
    // it). The headline range card + trend/recent are refreshed for the new window;
    // trend stays last-30-days context and recent stays the last 10 (server-fixed).
    dashboardRange() {
      this.fetchStats();
    },
  },
  mounted() {
    if (this.siteID) {
      this.fetchStats();
    }
    window.addEventListener('resize', this.onResize, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    fmt(n) {
      return formatStatNumber(n);
    },
    // Presentational: keep `viewportWidth` in sync so the label-thinning recomputes.
    onResize() {
      this.viewportWidth = window.innerWidth;
    },
    // Should bar `i` render its date label? Thinned + the most-recent day is anchored.
    showTrendLabel(i) {
      return shouldShowTrendLabel(i, this.trendBars.length, this.trendStride);
    },
    async fetchStats() {
      if (!this.siteID) return;
      // The action commits total/today + loading + access to the store and RETURNS
      // the full unwrapped payload (incl. trend/recent) to us. On failure it returns
      // { success:false } and we clear trend/recent so we never show stale rows next
      // to a "Couldn't load" / "Not available" state.
      const result = await this.$store.dispatch('botAdmin/fetchAppointmentsStats', { siteId: this.siteID });
      if (result && result.success) {
        this.trend = Array.isArray(result.trend) ? result.trend : [];
        this.recent = Array.isArray(result.recent) ? result.recent : [];
      } else {
        this.trend = [];
        this.recent = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../AppointmentCalendarModern.scss';

.appointment-stats {
  display: flex;
  flex-direction: column;
  gap: 1.5rem; /* Match Admin dashboard spacing (24px) */
  padding: 1rem; /* Add padding so content doesn't touch container edges */
  font-family: var(--aid-font);
}

/* Page Header */
.page-header {
  margin-bottom: 0;
}

.page-title {
  font-size: 18px;
  margin: 0;
  font-weight: 600;
  color: var(--aid-text);
  letter-spacing: -0.1px;
}

.page-subtitle {
  margin: 0.375rem 0 0;
  font-size: 13px;
  color: var(--aid-text-mute);
  max-width: 640px;
  line-height: 1.45;
}

/* Hero Stats Bar - Matching Admin Dashboard */
.hero-stats-bar {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem; /* 16px gap like Admin dashboard */
}

@media (max-width: 576px) {
  .hero-stats-bar {
    grid-template-columns: 1fr; /* Cards stack at phone width */
  }
}

.hero-stat-card {
  background: var(--aid-surface);
  border-radius: 10px;
  padding: 1.125rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  border: 1px solid var(--aid-border);
  box-shadow: var(--aid-shadow-card);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.hero-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--aid-shadow-md), 0 10px 22px rgba(15, 23, 42, 0.07);
  border-color: var(--aid-border-strong);
}

/* Teal glyph on a soft rounded square, matching the Dashboard tiles. */
.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  border: none;
  outline: none;

  i {
    border: none;
    outline: none;
    line-height: 1;
  }
}

.stat-icon.appointments-icon {
  background: var(--aid-accent-soft);
  color: var(--aid-accent);

  i {
    color: var(--aid-accent);
  }
}

.stat-icon.today-icon {
  background: #e3f3f3;
  color: var(--aid-success);

  i {
    color: var(--aid-success);
  }
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--aid-text);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
}

.stat-label {
  font-size: 13px;
  color: var(--aid-text-mute);
  font-weight: 500;
  white-space: nowrap;
}

/* Per-card honest states (loading / not-available / error replace the number) */
.stat-state {
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.2;
}

.stat-state-muted {
  color: var(--aid-text-dim);
}

.stat-state-error {
  color: var(--aid-danger-text);
}

.stat-state-loading {
  display: flex;
  align-items: center;
  min-height: 36px;
}

/* Panel Card Pattern - Matching Admin Dashboard */
.panel-card {
  background: var(--aid-surface);
  border-radius: 10px;
  border: 1px solid var(--aid-border);
  box-shadow: var(--aid-shadow-card);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--aid-border);
  background: var(--aid-surface2);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
  margin: 0;
}

.panel-icon {
  color: var(--aid-accent);
  font-size: 0.875rem;
}

.panel-body {
  padding: 1.25rem;
}

.panel-body.table-panel {
  padding: 0;
}

/* Chart Container */
.chart-container {
  min-height: 250px;
  position: relative;
}

/* Inline CSS bar chart (no chart library). Bars flex to fill the width on desktop;
   on a narrow phone they hit their min-width and the chart scrolls horizontally
   (with -webkit-overflow-scrolling for momentum) rather than squashing to slivers. */
.trend-chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 220px;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: 0.5rem;
}

/* Slim scrollbar so a scrolling chart doesn't look broken. */
.trend-chart::-webkit-scrollbar {
  height: 5px;
}
.trend-chart::-webkit-scrollbar-thumb {
  background: var(--aid-border);
  border-radius: 4px;
}

.trend-bar-col {
  flex: 1 1 0;
  min-width: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.trend-bar-track {
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.trend-bar-fill {
  width: 70%;
  max-width: 22px;
  min-height: 2px;
  background: var(--aid-accent);
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
}

/* A zero-booking day renders a faint baseline tick, not a full-height bar */
.trend-bar-fill.is-zero {
  background: var(--aid-border);
  min-height: 2px;
}

.trend-bar-label {
  margin-top: 0.375rem;
  font-size: 0.625rem;
  color: var(--aid-text-dim);
  white-space: nowrap;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  min-height: 0.75rem; /* reserve the row even when a label is thinned out, so bars align */
}

/* Data Table */
.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: var(--aid-surface2);
  padding: 0.75rem 1.25rem;
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  color: var(--aid-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--aid-border);
  white-space: nowrap;
}

.data-table td {
  padding: 0.875rem 1.25rem;
  font-size: 13px;
  color: var(--aid-text-mute);
  border-bottom: 1px solid var(--aid-border);
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tr:hover {
  background: var(--aid-surface2);
}

.patient-cell {
  font-weight: 600;
  color: var(--aid-text);
}

/* Table scrolls horizontally on narrow screens instead of squashing */
.table-panel {
  overflow-x: auto;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;
  color: var(--aid-text-mute);
  font-size: 13px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--aid-border);
  border-top-color: var(--aid-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-sm {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  color: var(--aid-border-strong);
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--aid-text);
  margin-bottom: 0.5rem;
}

.empty-text {
  font-size: 13px;
  color: var(--aid-text-mute);
  max-width: 320px;
}

/* Responsive */
@media (max-width: 768px) {
  .stat-icon {
    width: 38px;
    height: 38px;
    font-size: 1.125rem;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }
}

@media (max-width: 576px) {
  .appointment-stats {
    padding: 0.75rem;
    gap: 1rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem;
    font-size: 0.8125rem;
  }

  /* The recent-bookings table keeps all six columns and scrolls sideways rather
     than wrapping/cutting off; a sensible min-width forces the scroll. */
  .data-table {
    min-width: 560px;
  }

  .panel-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .panel-body {
    padding: 1rem;
  }

  .trend-chart {
    height: 180px;
  }

  /* Give each bar a touch-friendly min width so 30 days scroll instead of squashing
     into unreadable slivers on a phone. */
  .trend-bar-col {
    min-width: 18px;
  }
}
</style>
